import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import isNil from 'lodash-es/isNil';

import { Episode } from '~/src/common/lean-back/Episode';

import { useEpisodesList } from './useEpisodesList';

export type PlaybackContextValue = {
  episode: Episode;
  goToNextEpisode: () => void;
  isNextEpisodeAvailable: boolean;
};
const PlaybackContext = React.createContext<PlaybackContextValue | undefined>(undefined);
interface Props {
  initialEpisode: Episode;
}

export const PlaybackProvider: React.FC<PropsWithChildren<Props>> = ({
  initialEpisode,
  children,
}) => {
  const { episodesOrder } = useEpisodesList();
  const [episode, setEpisode] = useState<Episode>(initialEpisode);

  useEffect(() => {
    setEpisode(episode);
  }, [episode]);

  const isNextEpisodeAvailable = useMemo(() => {
    const currentEpisodeIdx = episodesOrder.findIndex(
      episodeItem => episode.id() === new Episode(episodeItem).id(),
    );
    const lastEpisodeIdx = episodesOrder.length - 1;
    if (currentEpisodeIdx < lastEpisodeIdx) {
      const nextEpisodeId = episodesOrder[currentEpisodeIdx + 1];
      if (!isNil(nextEpisodeId)) {
        return true;
      }
    }
    return false;
  }, [episode, episodesOrder]);

  const goToNextEpisode = useCallback(() => {
    const currentEpisodeIdx = episodesOrder.findIndex(
      episodeItem => episode.id() === new Episode(episodeItem).id(),
    );
    const lastEpisodeIdx = episodesOrder.length - 1;
    if (currentEpisodeIdx < lastEpisodeIdx) {
      const nextEpisodeData = episodesOrder[currentEpisodeIdx + 1];
      if (!isNil(nextEpisodeData)) {
        const nextEpisode = new Episode(nextEpisodeData);
        setEpisode(nextEpisode);
      }
    }
  }, [episode, episodesOrder]);

  const value = useMemo(() => {
    return {
      episode,
      goToNextEpisode,
      isNextEpisodeAvailable,
    };
  }, [episode, goToNextEpisode, isNextEpisodeAvailable]);

  if (isNil(episode.mediaId())) {
    return null;
  }
  return <PlaybackContext.Provider value={value}>{children}</PlaybackContext.Provider>;
};

export function usePlayback() {
  const context = React.useContext(PlaybackContext);
  if (typeof context === 'undefined') {
    throw new Error('usePlayback must be used within a PlaybackContext');
  }
  return context;
}
